import { Box, Breakpoint, SxProps } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";

type ResponsiveValue<T> = {
  [key in Breakpoint]?: T;
};

type AnimatedViewProps = {
  children: React.ReactNode;
  animation?: "slideIn" | "fadeIn" | "blurIn" | "popIn";
  sx?: SxProps;
  duration?: number | ResponsiveValue<number>;
  delay?: number | ResponsiveValue<number>;
};

export const AnimatedView = ({
  children,
  duration,
  delay = 0,
  sx = {},
  animation = "slideIn"
}: AnimatedViewProps) => {
  const domRef = useRef<Element>();
  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setVisible(true);

        if (domRef.current instanceof Element) {
          observer.unobserve(domRef.current as Element);
        }
      }
    });

    if (domRef.current instanceof Element) {
      observer.observe(domRef.current as Element);
    }

    return () => observer.disconnect();
  }, []);

  const createResponsiveValue = (
    value: ResponsiveValue<number>,
    style: string
  ): ResponsiveValue<any> => {
    const responsiveValue: ResponsiveValue<any> = {};

    for (const key in value) {
      const breakpoint = key as Breakpoint;
      const val = value[breakpoint];
      if (val !== undefined) {
        responsiveValue[breakpoint] = style.replaceAll("{{value}}", val.toString());
      }
    }

    return responsiveValue;
  };

  const transitionDelay = useMemo(
    () =>
      typeof delay === "number" || !delay
        ? `${delay || 0}ms`
        : createResponsiveValue(delay as ResponsiveValue<number>, `{{value}}ms`),
    [delay]
  );

  const animations: Record<string, SxProps> = {
    slideIn: {
      transform: isVisible ? "none" : "translate(0, 10%)",
      transition:
        typeof duration === "number" || !duration
          ? `transform ${duration || 300}ms ease-out`
          : createResponsiveValue(
              duration as ResponsiveValue<number>,
              `transform {{value}}ms ease-out`
            ),
      transitionDelay: transitionDelay,
      willChange: "visibility",
      ...sx
    },
    fadeIn: {
      visibility: isVisible ? "visible" : "hidden",
      opacity: isVisible ? 1 : 0,
      transition:
        typeof duration === "number" || !duration
          ? `opacity ${duration || 1000}ms ease-out, visibility ${duration || 1000}ms ease-out`
          : createResponsiveValue(
              duration as ResponsiveValue<number>,
              `opacity {{value}}ms ease-out, visibility {{value}}ms ease-out`
            ),
      transitionDelay: transitionDelay,
      willChange: "opacity, visibility",
      ...sx
    },
    blurIn: {
      visibility: isVisible ? "visible" : "hidden",
      filter: isVisible ? "blur(0)" : "blur(20px)",
      transition:
        typeof duration === "number" || !duration
          ? `filter ${duration || 300}ms ease-out`
          : createResponsiveValue(
              duration as ResponsiveValue<number>,
              `filter {{value}}ms ease-out`
            ),
      transitionDelay: transitionDelay,
      willChange: "filter",
      ...sx
    },
    popIn: {
      transform: isVisible ? "scale(1)" : "scale(0.5)",
      transition:
        typeof duration === "number" || !duration
          ? `transform ${duration || 500}ms ease-out`
          : createResponsiveValue(
              duration as ResponsiveValue<number>,
              `transform {{value}}ms ease-out`
            ),
      transitionDelay: transitionDelay,
      willChange: "transform",
      ...sx
    }
  };

  return (
    <Box ref={domRef} sx={animations[animation]}>
      {children}
    </Box>
  );
};
